/**
 * Cola de mutaciones offline, agnóstica de almacenamiento y de transporte.
 *
 * No arranca temporizadores: es el consumidor quien decide cuándo llamar a
 * `flush()` (al recuperar la red, al volver la app a primer plano, tras un
 * `enqueue`, …). Tampoco usa APIs de Node ni del DOM, así que corre igual en
 * el navegador, en Hermes/React Native y en tests.
 */
const HEX = '0123456789abcdef';
/**
 * Genera un UUID v4 válido con la aleatoriedad que se le pase. No usa
 * `crypto.randomUUID` porque no existe en Hermes; para identificadores con
 * garantías criptográficas, pásale un `random` respaldado por el sistema.
 */
export function createIdGenerator(random = Math.random) {
    return () => {
        let out = '';
        for (let i = 0; i < 36; i += 1) {
            if (i === 8 || i === 13 || i === 18 || i === 23) {
                out += '-';
                continue;
            }
            if (i === 14) {
                out += '4';
                continue;
            }
            const nibble = Math.floor(random() * 16) % 16;
            out += i === 19 ? HEX.charAt((nibble & 0x3) | 0x8) : HEX.charAt(nibble);
        }
        return out;
    };
}
/** Almacenamiento en memoria: para tests y para la web mientras no haya IndexedDB. */
export class MemorySyncStorage {
    ops = new Map();
    constructor(initial = []) {
        for (const op of initial)
            this.ops.set(op.id, { ...op });
    }
    async getAll() {
        return Array.from(this.ops.values(), (op) => ({ ...op }));
    }
    async put(op) {
        this.ops.set(op.id, { ...op });
    }
    async remove(id) {
        this.ops.delete(id);
    }
    /** Sólo para tests e inspección: número de operaciones guardadas. */
    get size() {
        return this.ops.size;
    }
}
/** Error con el que se descarta una operación cuyo `kind` no tiene handler. */
export class MissingSyncHandlerError extends Error {
    kind;
    constructor(kind) {
        super(`sync: no hay handler para el tipo "${kind}"`);
        this.name = 'MissingSyncHandlerError';
        this.kind = kind;
    }
}
function describeError(error) {
    if (error instanceof Error)
        return error.message;
    if (typeof error === 'string')
        return error;
    return String(error);
}
/** Amplitud del jitter aplicado al backoff: ±20 %. */
const JITTER = 0.2;
function compareOps(a, b) {
    if (a.createdAt !== b.createdAt)
        return a.createdAt - b.createdAt;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/**
 * Cola FIFO con reintentos exponenciales y descarte controlado.
 *
 * ```ts
 * const queue = new SyncQueue({
 *   storage: new MemorySyncStorage(),
 *   handlers: { 'setLog.create': (op) => api.createSetLog(op.payload) },
 *   isPermanentError: (e) => e instanceof ApiError && e.status >= 400 && e.status < 500,
 * });
 * await queue.enqueue('setLog.create', body, body.clientId);
 * await queue.flush();
 * ```
 */
export class SyncQueue {
    storage;
    handlers;
    now;
    random;
    idGenerator;
    maxAttempts;
    baseDelayMs;
    maxDelayMs;
    onChange;
    onDeadLetter;
    isPermanentError;
    isTransientError;
    isActive;
    inFlight = null;
    constructor(options) {
        this.storage = options.storage;
        this.handlers = { ...options.handlers };
        this.now = options.now ?? (() => Date.now());
        this.random = options.random ?? Math.random;
        this.idGenerator = options.idGenerator ?? createIdGenerator(this.random);
        this.maxAttempts = options.maxAttempts ?? 8;
        this.baseDelayMs = options.baseDelayMs ?? 2000;
        this.maxDelayMs = options.maxDelayMs ?? 5 * 60_000;
        this.onChange = options.onChange;
        this.onDeadLetter = options.onDeadLetter;
        this.isPermanentError = options.isPermanentError ?? (() => false);
        this.isTransientError = options.isTransientError ?? (() => false);
        this.isActive = options.isActive ?? (() => true);
    }
    /** Encola una mutación lista para enviarse en el próximo `flush`. */
    async enqueue(kind, payload, id) {
        const timestamp = this.now();
        const op = {
            id: id ?? this.idGenerator(),
            kind,
            payload,
            createdAt: timestamp,
            attempts: 0,
            nextAttemptAt: timestamp,
        };
        await this.storage.put(op);
        await this.notify();
        return op;
    }
    /** Número de operaciones en la cola, incluidas las que aún no toca reintentar. */
    async pending() {
        const all = await this.storage.getAll();
        return all.length;
    }
    /** Operaciones en la cola, en orden de envío. */
    async list() {
        const all = await this.storage.getAll();
        return all.sort(compareOps);
    }
    /**
     * Envía las operaciones cuyo `nextAttemptAt` ya venció, en orden de creación.
     * Es single-flight: las llamadas concurrentes comparten la misma promesa.
     */
    flush() {
        if (this.inFlight)
            return this.inFlight;
        const running = this.run().finally(() => {
            this.inFlight = null;
        });
        this.inFlight = running;
        return running;
    }
    /** Adelanta todos los reintentos pendientes y sincroniza de inmediato. */
    async retryNow() {
        const timestamp = this.now();
        const all = await this.storage.getAll();
        for (const op of all) {
            if (op.nextAttemptAt > timestamp) {
                await this.storage.put({ ...op, nextAttemptAt: timestamp });
            }
        }
        return this.flush();
    }
    /** Retardo del siguiente intento: exponencial con techo y jitter de ±20 %. */
    backoffDelay(attempts) {
        const exponential = this.baseDelayMs * 2 ** Math.max(0, attempts - 1);
        const capped = Math.min(this.maxDelayMs, exponential);
        const jitter = 1 + (this.random() * 2 - 1) * JITTER;
        return Math.max(0, Math.round(capped * jitter));
    }
    async run() {
        const result = { processed: 0, succeeded: 0, failed: 0, deferred: 0 };
        const startedAt = this.now();
        const all = await this.storage.getAll();
        const due = all.filter((op) => op.nextAttemptAt <= startedAt).sort(compareOps);
        for (const op of due) {
            // La cuenta ya no está activa: no sale nada más y lo que queda espera
            // intacto a que esa misma cuenta vuelva a entrar.
            if (!this.isActive())
                break;
            result.processed += 1;
            const handler = this.handlers[op.kind];
            if (!handler) {
                const attempted = { ...op, attempts: op.attempts + 1 };
                await this.deadLetter(attempted, new MissingSyncHandlerError(op.kind));
                result.failed += 1;
                continue;
            }
            try {
                await handler(op);
                await this.storage.remove(op.id);
                result.succeeded += 1;
            }
            catch (error) {
                // Un fallo de transporte no dice nada de la operación: se reintenta sin
                // gastar intentos. Sólo los que el servidor (o el handler) achacan a la
                // operación la acercan al descarte.
                const transient = !this.isPermanentError(error) && this.isTransientError(error);
                const attempted = {
                    ...op,
                    attempts: op.attempts + 1,
                    failures: (op.failures ?? 0) + (transient ? 0 : 1),
                    lastError: describeError(error),
                };
                if (this.isPermanentError(error) ||
                    (!transient && (attempted.failures ?? 0) >= this.maxAttempts)) {
                    await this.deadLetter(attempted, error);
                    result.failed += 1;
                }
                else {
                    attempted.nextAttemptAt = this.now() + this.backoffDelay(attempted.attempts);
                    await this.storage.put(attempted);
                    result.deferred += 1;
                }
            }
        }
        if (result.processed > 0)
            await this.notify();
        return result;
    }
    async deadLetter(op, error) {
        const marked = { ...op, lastError: op.lastError ?? describeError(error) };
        await this.storage.remove(marked.id);
        this.onDeadLetter?.(marked, error);
    }
    async notify() {
        if (!this.onChange)
            return;
        this.onChange(await this.pending());
    }
}
//# sourceMappingURL=syncQueue.js.map